import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  MapPin, Eye, Send, CheckCircle2, Crown, Trophy, Sparkles,
  Camera, ArrowRight, Clock, Loader2, Lock, Trash2,
} from "lucide-react";
import OGMeta from "@/components/OGMeta";
import PhotoUploader from "@/components/PhotoUploader";
import "@/styles/met-founder.css";
import "@/styles/pulse.css"; // reuse pulse-faq-item accordion styles

function PresenceTag({ presence, S }) {
  const mode = presence?.mode || "off_site";
  if (mode === "off_site") {
    return (
      <div className="met-presence-badge off" data-testid="met-presence-off">
        <span className="dot" />
        {S.off_site_label}
      </div>
    );
  }
  if (mode === "stealth") {
    return (
      <div className="met-presence-badge" style={{ borderColor: "#D7A62A", color: "#D7A62A", background: "rgba(215,166,42,0.10)" }}>
        <span className="dot" style={{ background: "#D7A62A" }} />
        Stealth mode · check the homepage clue
      </div>
    );
  }
  // on_site
  return (
    <div className="met-presence-badge" data-testid="met-presence-on">
      <span className="dot" />
      Spotted now · {presence.city || "out there"}
      {presence.location_note ? <span style={{ opacity: 0.7, textTransform: "none", marginLeft: 6, letterSpacing: 0 }}>· {presence.location_note}</span> : null}
    </div>
  );
}

function SubmitForm({ S, onSubmitted, latestMine }) {
  const { user } = useAuth();
  const [where, setWhere] = useState("");
  const [city, setCity] = useState("");
  const [whenText, setWhenText] = useState("");
  const [note, setNote] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) {
    return (
      <div className="loot-card-flat p-8 text-center" data-testid="met-signin-gate">
        <Lock size={28} className="mx-auto text-[#D7A62A]" />
        <div className="font-display uppercase text-2xl mt-3">Sign in to claim your meet</div>
        <p className="text-[#F5F2E8]/60 mt-2 text-sm">Hunters only — keeps the badge real.</p>
        <Link to="/login" className="btn-loot inline-flex items-center gap-2 mt-5">
          Sign in <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  const pending = latestMine?.status === "pending";

  const submit = async () => {
    if (!where.trim()) return toast.error("Where did you meet me?");
    setBusy(true);
    try {
      const { data } = await api.post("/founder/met-submission", {
        where: where.trim(),
        city: city.trim(),
        when_text: whenText.trim(),
        note: note.trim(),
        photo_url: photoUrl,
      });
      toast.success("Submitted! I'll verify within 24 hours 👊");
      onSubmitted?.(data?.id);
      // Clear form
      setWhere(""); setCity(""); setWhenText(""); setNote(""); setPhotoUrl("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't submit. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="loot-card-flat p-6 md:p-8 space-y-4" data-testid="met-submit-form">
      <div>
        <div className="overline" style={{ color: "#76B82A" }}>Your turn</div>
        <h3 className="font-display uppercase text-3xl tracking-tight mt-1">Claim your meet.</h3>
        <p className="text-[#F5F2E8]/60 text-sm mt-1">
          Drop the details below. I review every submission personally — usually same day.
        </p>
      </div>

      {pending && (
        <div className="p-3 border" style={{ borderColor: "#D7A62A", background: "rgba(215,166,42,0.06)" }}>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#D7A62A] flex items-center gap-2">
            <Clock size={12} /> Pending review
          </div>
          <p className="text-sm text-[#F5F2E8]/75 mt-1">
            Submitted {latestMine.created_at?.slice(0, 10)} · {latestMine.where}{latestMine.city ? ` · ${latestMine.city}` : ""}
          </p>
          <p className="font-mono text-[10px] text-[#F5F2E8]/50 mt-1">
            Re-submitting below will REPLACE your pending claim.
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">Where did we meet? *</span>
          <input
            className="loot-input mt-1"
            placeholder="Java Hut · the Greeneville Hike · Sponsor meeting at..."
            value={where}
            onChange={(e) => setWhere(e.target.value)}
            maxLength={200}
            data-testid="met-where"
          />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">City</span>
          <input
            className="loot-input mt-1"
            placeholder="Bristol, Erwin, Jonesborough…"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            maxLength={80}
            data-testid="met-city"
          />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">When?</span>
          <input
            className="loot-input mt-1"
            placeholder="This morning · Saturday around noon · 2 weeks ago"
            value={whenText}
            onChange={(e) => setWhenText(e.target.value)}
            maxLength={80}
            data-testid="met-when"
          />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 flex items-center gap-1">
            <Camera size={12} /> Selfie or photo (optional — speeds up verification)
          </span>
          <div className="mt-1">
            <PhotoUploader value={photoUrl} onChange={setPhotoUrl} testid="met-photo" />
          </div>
        </label>
      </div>
      <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">Note · what'd we talk about? (optional)</span>
        <textarea
          rows={3}
          maxLength={1500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Coffee meet — talked about the new mystery hunts and how badge tiers should work…"
          className="loot-input mt-1 w-full"
          data-testid="met-note"
        />
      </label>
      <button
        onClick={submit}
        disabled={busy}
        className="btn-loot inline-flex items-center gap-2"
        data-testid="met-submit-btn"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        {busy ? "Sending…" : (S.cta_button_label || "Submit my meet")}
      </button>
    </div>
  );
}

export default function MetTheFounderPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [mine, setMine] = useState([]);

  const load = async () => {
    try {
      const { data: d } = await api.get("/founder/met-page");
      setData(d);
    } catch {
      setData({ settings: {}, faqs: [], honor_roll: [], stats: { total_meets: 0, cities: 0, hunters: 0 }, presence: { mode: "off_site" } });
    }
  };
  const loadMine = async () => {
    if (!user) return setMine([]);
    try {
      const { data: d } = await api.get("/founder/met-submission/me");
      setMine(d?.items || []);
    } catch { setMine([]); }
  };
  useEffect(() => { load(); loadMine(); }, [user?.id]); // eslint-disable-line

  const S = useMemo(() => ({
    hero_eyebrow: "WANTED · ALIVE",
    hero_title_pre: "Spot the",
    hero_title_accent: "founder.",
    hero_subtitle: "Mickey runs The Local Loot. He's out in the Tri-Cities most days. Catch him in the wild and mention the app to earn the legendary Met-the-Founder badge.",
    founder_photo_url: "",
    founder_name: "Mickey",
    founder_handle: "@mickey",
    current_status_eyebrow: "Last seen",
    off_site_label: "Off the trail — out of pocket today",
    how_title: "How it works.",
    how_step_1: "See me out and about — coffee shop, hike, sponsor visit, store, anywhere.",
    how_step_2: "Say hi. Mention The Local Loot. Snap a quick selfie if you want bragging rights.",
    how_step_3: "Come back to this page and submit your claim. I'll verify and grant the badge within 24 hours.",
    ladder_title: "The Founder Badge Ladder",
    ladder_intro: "Meeting me unlocks a trio of legendary badges as your collection grows:",
    ladder_1_label: "🏆 Found the Founder",
    ladder_1_body: "First meet — anywhere, any time.",
    ladder_2_label: "🌟 Loot Disciple",
    ladder_2_body: "Met me in 3 different cities. You're chasing the loot.",
    ladder_3_label: "👑 Local Legend",
    ladder_3_body: "Met me in 10 different cities. Reserved for true road dogs.",
    cta_button_label: "Submit my meet",
    honor_roll_title: "Wall of meets.",
    honor_roll_subtitle: "Hunters who've crossed paths with the founder.",
    faq_eyebrow: "Frequently asked",
    faq_title: "Questions about meets.",
    ...(data?.settings || {}),
  }), [data]);

  const stats = data?.stats || { total_meets: 0, cities: 0, hunters: 0 };
  const honor = data?.honor_roll || [];
  const latestMine = mine[0];

  return (
    <div className="py-8 space-y-10" data-testid="met-page">
      <OGMeta
        title={`Met the Founder · The Local Loot`}
        description="Catch the founder out in the wild and earn the legendary Met-the-Founder badge."
      />

      {/* HERO — wanted poster */}
      <section className="met-hero" data-testid="met-hero">
        <div className="met-wanted-stamp">Wanted · Reward</div>
        <div className="relative flex flex-col md:flex-row items-start gap-6 md:gap-10">
          <div className="met-photo-frame" data-testid="met-photo-frame">
            {S.founder_photo_url ? (
              <img src={S.founder_photo_url} alt={S.founder_name} />
            ) : (
              <span className="placeholder">{(S.founder_name || "M")[0]}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline" style={{ color: "#D7A62A" }}>{S.hero_eyebrow}</div>
            <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight mt-2 leading-[0.95]">
              {S.hero_title_pre} <span style={{ color: "#D7A62A" }}>{S.hero_title_accent}</span>
            </h1>
            <p className="text-[#F5F2E8]/80 mt-4 text-base md:text-lg leading-relaxed max-w-2xl whitespace-pre-line">
              {S.hero_subtitle}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55">
                {S.current_status_eyebrow}:
              </div>
              <PresenceTag presence={data?.presence} S={S} />
            </div>

            {/* Stats inline */}
            <div className="mt-6 flex flex-wrap gap-3">
              {[
                { k: "Total meets", v: stats.total_meets, color: "#D7A62A", i: <Sparkles size={14} /> },
                { k: "Cities", v: stats.cities, color: "#76B82A", i: <MapPin size={14} /> },
                { k: "Hunters", v: stats.hunters, color: "#F5F2E8", i: <Trophy size={14} /> },
              ].map((s) => (
                <div key={s.k} className="loot-card-flat p-3 px-4 flex items-center gap-2" data-testid={`met-stat-${s.k.replace(/\s+/g, "-").toLowerCase()}`}>
                  <span style={{ color: s.color }}>{s.i}</span>
                  <div>
                    <div className="font-display text-2xl leading-none" style={{ color: s.color }}>{s.v}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55">{s.k}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="met-how">
        <div className="overline mb-2" style={{ color: "#76B82A" }}>3 steps</div>
        <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight mb-5">{S.how_title}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[S.how_step_1, S.how_step_2, S.how_step_3].map((body, i) => (
            <div key={i} className="loot-card-flat p-5 flex gap-3" data-testid={`met-step-${i + 1}`}>
              <div className="met-step-badge">{i + 1}</div>
              <p className="text-sm text-[#F5F2E8]/80 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SUBMIT */}
      <section id="submit" data-testid="met-submit-section">
        <SubmitForm S={S} latestMine={latestMine} onSubmitted={() => { loadMine(); load(); }} />
        {user && mine.length > 0 && (
          <div className="mt-4 loot-card-flat p-4" data-testid="met-mine">
            <div className="overline mb-2">Your submissions</div>
            <div className="space-y-1.5">
              {mine.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-2 text-sm py-1 border-b border-[#F5F2E8]/8 last:border-0">
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55 mr-2">
                      {m.created_at?.slice(0, 10)}
                    </span>
                    <span className="truncate">{m.where}{m.city ? ` · ${m.city}` : ""}</span>
                  </div>
                  <span
                    className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5"
                    style={{
                      color: m.status === "approved" ? "#76B82A" : m.status === "rejected" ? "#E53935" : "#D7A62A",
                      borderLeft: `2px solid ${m.status === "approved" ? "#76B82A" : m.status === "rejected" ? "#E53935" : "#D7A62A"}`,
                    }}
                    data-testid={`met-mine-status-${m.id}`}
                  >
                    {m.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* LADDER */}
      <section data-testid="met-ladder">
        <div className="overline mb-2" style={{ color: "#D7A62A" }}>Climb the ranks</div>
        <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight mb-2">{S.ladder_title}</h2>
        <p className="text-[#F5F2E8]/65 mb-5 max-w-2xl text-sm leading-relaxed">{S.ladder_intro}</p>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { l: S.ladder_1_label, b: S.ladder_1_body, t: 1, icon: Trophy, color: "#D7A62A" },
            { l: S.ladder_2_label, b: S.ladder_2_body, t: 2, icon: Sparkles, color: "#76B82A" },
            { l: S.ladder_3_label, b: S.ladder_3_body, t: 3, icon: Crown, color: "#E53935" },
          ].map((tier) => (
            <div key={tier.t} className="met-ladder-card" data-tier={String(tier.t)} data-testid={`met-tier-${tier.t}`}>
              <div className="flex items-center gap-2" style={{ color: tier.color }}>
                <tier.icon size={20} />
                <span className="overline" style={{ color: tier.color }}>Tier {tier.t}</span>
              </div>
              <div className="font-display uppercase text-xl mt-2 leading-tight">{tier.l}</div>
              <p className="text-sm text-[#F5F2E8]/70 mt-2 leading-relaxed">{tier.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HONOR ROLL */}
      {honor.length > 0 && (
        <section data-testid="met-honor">
          <div className="overline mb-2" style={{ color: "#D7A62A" }}>{honor.length} hunters strong</div>
          <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight mb-1">{S.honor_roll_title}</h2>
          <p className="text-[#F5F2E8]/60 mb-5 text-sm">{S.honor_roll_subtitle}</p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {honor.map((h, i) => (
              <Link
                key={`${h.username}-${i}`}
                to={`/u/${h.username}`}
                className="met-honor-tile flex items-center justify-between gap-2 no-underline"
                data-testid={`met-honor-${h.username}`}
              >
                <div className="min-w-0">
                  <div className="font-display uppercase text-sm truncate text-[#F5F2E8]">@{h.username || "anon"}</div>
                  {h.city && (
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55 truncate">
                      <MapPin size={9} className="inline mr-1" />{h.city}
                    </div>
                  )}
                </div>
                <Trophy size={14} style={{ color: "#D7A62A", flexShrink: 0 }} />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQs */}
      <section data-testid="met-faqs">
        <div className="overline mb-2" style={{ color: "#D7A62A" }}>{S.faq_eyebrow}</div>
        <h2 className="font-display uppercase text-3xl tracking-tight mb-4">{S.faq_title}</h2>
        <div className="loot-card-flat px-5 md:px-8 py-2">
          {(data?.faqs || []).map((f, i) => (
            <details key={i} className="pulse-faq-item" data-testid={`met-faq-${i}`}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
